import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Switch, Tooltip } from "@material-ui/core";
import { Toast } from "../../util/Toast";
import { alert, permissionError, warning } from "../../util/Alert";
import { useSelector } from "react-redux";

const DailyTaskRestrictionManagement = () => {
  const [restrictions, setRestrictions] = useState([]);
  const [uniqueId, setUniqueId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const hasPermission = useSelector((state) => state.admin.admin.flag);

  const loadRestrictions = async () => {
    try {
      const response = await axios.get("dailyTask/restrictions/admin");
      if (!response.data.status) throw new Error(response.data.message);
      setRestrictions(response.data.data.restrictions || []);
    } catch (error) {
      Toast("error", error.response?.data?.message || error.message || "Unable to load daily task restrictions.");
    }
  };

  useEffect(() => {
    loadRestrictions();
  }, []);

  const createRestriction = async (event) => {
    event.preventDefault();
    if (!hasPermission) return permissionError();
    if (!uniqueId.trim()) return Toast("error", "User Unique ID is required.");
    try {
      setSaving(true);
      const response = await axios.post("dailyTask/restrictions/admin", {
        uniqueId: uniqueId.trim(),
        reason: reason.trim(),
      });
      if (!response.data.status) throw new Error(response.data.message);
      setRestrictions((current) => [response.data.data, ...current.filter((item) => item._id !== response.data.data._id)]);
      setUniqueId("");
      setReason("");
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response?.data?.message || error.message || "Unable to restrict this user.");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (restriction) => {
    if (!hasPermission) return permissionError();
    try {
      const response = await axios.patch(
        `dailyTask/restrictions/admin/${restriction._id}/status`,
        { isActive: !restriction.isActive }
      );
      if (!response.data.status) throw new Error(response.data.message);
      setRestrictions((current) => current.map((item) => item._id === restriction._id ? response.data.data : item));
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response?.data?.message || error.message || "Unable to update the restriction.");
    }
  };

  const deleteRestriction = (restriction) => {
    warning().then(async (confirmed) => {
      if (!confirmed) return;
      if (!hasPermission) return permissionError();
      try {
        const response = await axios.delete(`dailyTask/restrictions/admin/${restriction._id}`);
        if (!response.data.status) throw new Error(response.data.message);
        setRestrictions((current) => current.filter((item) => item._id !== restriction._id));
        alert("Deleted!", "Daily task restriction has been deleted.", "success");
      } catch (error) {
        Toast("error", error.response?.data?.message || error.message || "Unable to delete the restriction.");
      }
    });
  };

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3 className="mb-3 text-white">Daily Task Restriction</h3>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/admin/dashboard" className="text-danger">Dashboard</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Daily Task Restriction</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <form className="row g-3" onSubmit={createRestriction}>
            <div className="col-md-4"><label className="form-label">User Unique ID</label><input className="form-control" value={uniqueId} onChange={(event) => setUniqueId(event.target.value)} placeholder="Enter user unique ID" /></div>
            <div className="col-md-6"><label className="form-label">Reason (optional)</label><input className="form-control" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why is this user restricted?" /></div>
            <div className="col-md-2 d-flex align-items-end"><button className="btn btn-danger w-100" type="submit" disabled={saving}>{saving ? "Saving..." : "Restrict"}</button></div>
          </form>
        </div>
      </div>
      <div className="card mt-4">
        <div className="card-body card-overflow">
          <table className="table table-striped">
            <thead className="text-center"><tr><th>User</th><th>Unique ID</th><th>Reason</th><th>Active</th><th>Delete</th></tr></thead>
            <tbody className="text-center">
              {restrictions.length ? restrictions.map((restriction) => (
                <tr key={restriction._id}>
                  <td>{restriction.user?.username || restriction.user?.name || "-"}</td><td>{restriction.user?.uniqueId || "-"}</td><td>{restriction.reason || "-"}</td>
                  <td><Tooltip title={restriction.isActive ? "Deactivate restriction" : "Activate restriction"}><Switch checked={restriction.isActive} onChange={() => toggleStatus(restriction)} color="primary" /></Tooltip></td>
                  <td><button type="button" className="btn bg-danger btn-sm" onClick={() => deleteRestriction(restriction)} aria-label={`Delete restriction for ${restriction.user?.username || "user"}`}><i className="fas text-light fa-trash-alt fa-lg" /></button></td>
                </tr>
              )) : <tr><td colSpan="5">No daily task restrictions found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default DailyTaskRestrictionManagement;
